import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MaskedLine, FadeUp, ParallaxImage } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { IMAGES, usePageTitle } from "@/lib/site";

const VALUES = [
  { title: "Accuracy", desc: "Numbers that tie out, every time. Precision is not a feature — it is the baseline." },
  { title: "Discretion", desc: "Your financials are handled with strict confidentiality and care, always." },
  { title: "Accessibility", desc: "Plain language, patient answers, and a team that picks up the phone." },
  { title: "Accountability", desc: "Clear scope, clear cadence, clear fees. You always know what to expect." },
];

export default function About() {
  usePageTitle("About — CalCor Solutions | Seattle Accounting Firm");

  return (
    <>
      <section data-testid="about-hero" className="pt-36 pb-20 sm:pt-44 lg:pb-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <MaskedLine delay={0.15} className="text-xs font-semibold uppercase tracking-[0.25em] text-pine">
            About CalCor Solutions
          </MaskedLine>
          <h1 className="mt-6 max-w-4xl font-serif text-5xl font-medium leading-[1.05] tracking-tighter text-ink sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.3}>Good books are</MaskedLine>
            <MaskedLine delay={0.45}>
              <em className="text-pine">a form of care.</em>
            </MaskedLine>
          </h1>
          <FadeUp delay={0.6}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-zinc-600 sm:text-lg">
              CalCor Solutions is a Seattle-based firm serving small businesses and tribal
              governments — in person across the Pacific Northwest and remotely, anywhere.
              We believe careful, consistent financial work is how organizations honor the
              people who depend on them.
            </p>
          </FadeUp>
        </div>
      </section>

      <section data-testid="about-image-band" className="px-6 lg:px-10">
        <FadeUp className="mx-auto max-w-7xl">
          <ParallaxImage
            src={IMAGES.seattle}
            alt="Seattle skyline"
            testId="about-seattle-image"
            className="aspect-[16/8] w-full rounded-3xl"
          />
        </FadeUp>
      </section>

      {/* ————— Story ————— */}
      <section data-testid="about-story" className="py-24 lg:py-32">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <FadeUp>
              <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-pine">
                <span className="h-px w-10 bg-pine" />
                Our story
              </p>
              <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tighter text-ink sm:text-5xl">
                Built for the organizations <em className="text-pine">others overlook.</em>
              </h2>
            </FadeUp>
          </div>
          <div className="lg:col-span-7">
            <FadeUp delay={0.1}>
              <div className="space-y-6 text-base leading-relaxed text-zinc-600 sm:text-lg">
                <p>
                  CalCor Solutions was founded on a simple observation: small businesses and
                  tribal governments carry enormous responsibility, yet are often underserved
                  by firms built for much larger clients. We set out to change that — bringing
                  disciplined accounting, audit preparation, and bookkeeping to organizations
                  that deserve senior-level attention.
                </p>
                <p>
                  For tribal governments, that means fluency in fund accounting, grant
                  compliance, and Uniform Guidance single audits — delivered with respect for
                  sovereignty, council rhythms, and community priorities. For small
                  businesses, it means books that are always current and an advisor who
                  answers in plain language.
                </p>
                <p>
                  And because our practice is remote-capable from the ground up, geography is
                  never a barrier. Secure tools, scheduled check-ins, and a protected client
                  portal keep every engagement close, wherever you are.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <Marquee items={["Precision", "Discretion", "Partnership", "Accountability", "Care"]} />

      {/* ————— Values ————— */}
      <section data-testid="values-section" className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-pine">
              <span className="h-px w-10 bg-pine" />
              What we stand for
            </p>
            <h2 className="mt-6 max-w-2xl font-serif text-4xl leading-tight tracking-tighter text-ink sm:text-5xl">
              Values that show up <em className="text-pine">in the work.</em>
            </h2>
          </FadeUp>
          <div className="mt-16 grid gap-px overflow-hidden rounded-3xl border border-black/10 bg-black/10 sm:grid-cols-2">
            {VALUES.map((v, i) => (
              <FadeUp key={v.title} delay={i * 0.08} className="bg-white">
                <div data-testid={`value-${v.title.toLowerCase()}`} className="h-full p-10 transition-colors duration-300 hover:bg-paper">
                  <span className="font-serif text-3xl italic text-pine/30">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-5 font-serif text-2xl tracking-tight text-ink">{v.title}</h3>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-zinc-600 sm:text-base">{v.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ————— Tribal governments ————— */}
      <section data-testid="tribal-section" className="border-y border-black/10 bg-white py-24 lg:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-6">
            <FadeUp>
              <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-pine">
                <span className="h-px w-10 bg-pine" />
                Tribal governments
              </p>
              <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tighter text-ink sm:text-5xl">
                Honored to serve <em className="text-pine">tribal nations.</em>
              </h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-600 sm:text-lg">
                We support tribal finance departments with fund accounting, indirect cost
                rate support, grant tracking, and audit preparation aligned to Uniform
                Guidance. Our work is paced to council calendars and delivered with the
                discretion and respect these engagements deserve.
              </p>
              <Link
                to="/contact"
                data-testid="tribal-cta-button"
                className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-pine transition-colors hover:text-clay"
              >
                Start a conversation
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </FadeUp>
          </div>
          <div className="lg:col-span-6">
            <FadeUp delay={0.15}>
              <ParallaxImage
                src={IMAGES.accountants}
                alt="Professionals reviewing documents together"
                testId="tribal-image"
                arch
                className="aspect-[4/5] w-full"
              />
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ————— CTA ————— */}
      <section data-testid="about-cta" className="py-24 lg:py-32">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <FadeUp>
            <h2 className="font-serif text-4xl leading-tight tracking-tighter text-ink sm:text-5xl">
              Let&rsquo;s put your finances <em className="text-pine">in careful hands.</em>
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-600 sm:text-lg">
              A short conversation is all it takes to know whether we are the right fit.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="about-cta-contact-button"
                className="group inline-flex items-center gap-2 rounded-full bg-pine px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-clay"
              >
                Request a consultation
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact#booking"
                data-testid="about-cta-booking-button"
                className="group inline-flex items-center gap-2 rounded-full border border-black/15 px-7 py-4 text-sm font-semibold text-ink transition-colors duration-300 hover:border-pine hover:text-pine"
              >
                Book an appointment
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
